
import { useMandateEnricher } from "./useMandateEnricher";
import { MandateViewModel } from "../ViewModels/MandateViewModel";

import {
    ActionStatusHelper,
    DomainDetailHook,
    useAppEnum,
    useDetailLifeCycle,
    useExportAction,
} from "@/Shared";

import { DOMAIN_CONFIGS, DomainHookProps } from "@/Shared/Config/domain-config";
import { useDocument, useMandate } from "@/OneLiners";
import { MandateModel } from "../Models";


/**
 * useMandateDetail hook
 *
 * Domain-specific detail hook for Mandate entities.
 * Orchestrates domain data enrichment, document handling,
 * deletion workflow, UI feedback (toasts, confirmation),
 * and navigation concerns.
 *
 * Responsibilities:
 * - Initialize the MandateViewModel when data is available.
 * - Enrich the mandate with associated documents.
 * - Handle delete operations with access control and confirmation.
 * - Handle PDF export and generation for the current mandate.
 * - Manage navigation (layout, error page) based on user permissions.
 * - Expose entity VM, action executor, and access rights to the UI layer.
 *
 * @see DomainHookProps
 * @see DomainDetailHook<MandateViewModel>
 *
 * @param props DomainHookProps
 * @returns DomainDetailHook<MandateViewModel>
 *
 * Author: Samano CASTRE
 * Date: March 2, 2026
 */
export function useMandateDetail(props: DomainHookProps): DomainDetailHook<MandateViewModel> {
    const { permissionHook, accessRight, uiFeedbackHooks } = props;
    const { domainType, domainLabel } = DOMAIN_CONFIGS.Mandate;

    const { MandateEnums, DocumentEnums, NeedEnums, PropertyEnums } = useAppEnum();

    /**
     * Mandate domain CRUD operations and state
     */
    const mandateHook = useMandate();

    /**
     * Document domain CRUD operations and state
     */
    const { dataMap: documentMap, stateResponse: documentResponse } = useDocument();


    /**
     * Mandate domain data and operations
     * Provided by the mandate enricher facade
     */
    const {
        getStrictEnrichedMandate,
        findByDocumentResource,
        enricherResponse,
    } = useMandateEnricher();


    /**
     * Combined response from mandate and document enrichment
     */
    const response = {
        status: ActionStatusHelper.combine(
            mandateHook.stateResponse.status,
            documentResponse.status,
            enricherResponse.status,
            permissionHook.stateResponse.status
        ),
        error: mandateHook.stateResponse.error ??
            documentResponse.error ??
            enricherResponse.error
    }

    const { isSucceeded: generateIsSucceed } = ActionStatusHelper.is(mandateHook.generateAction.response.status);

    /**
     * Detail life cycle hook
     *
     * Handles:
     * - Entity resolution (queryId / entityId)
     * - ViewModel construction
     * - Post-fetch or post-update handling
     * - Provides public API to the UI layer
     */
    const lifeCycleHook = useDetailLifeCycle({
        response,
        domainType,
        props,
        entityHook: mandateHook,
        buildVM: (currentEntity: MandateModel, isDisplaying: boolean) => {
            const filteredDocumentMap = findByDocumentResource(documentMap, domainType, currentEntity.id);
            return new MandateViewModel({
                mandate: getStrictEnrichedMandate(currentEntity),
                documentMap: filteredDocumentMap,
                DocumentEnums,
                NeedEnums,
                PropertyEnums,
                viewEnums: MandateEnums,
                isDisplaying,
                accessRight
            })
        },
        deps: [generateIsSucceed]
    });

     const {entityVM, requestId} = lifeCycleHook;
    
    const mandateType = entityVM?.model.mandateType;
    const {computeUIActions} = useExportAction<MandateModel, MandateViewModel>({
        accessRight : props.accessRight,
        uiFeedbackHooks: props.uiFeedbackHooks,
        entityVM : entityVM,
        domainType,
        domainLabel,
        entityHook: mandateHook,
        supportedDocTypes : mandateType ? [mandateType] : undefined,
        noFormDocTypes : mandateType ? [mandateType] : undefined,
    });

    /**
     * Public API exposed to the UI layer.
     */
    return {
        ...lifeCycleHook,
        computeUIActions,
    }
}